import { porcento, tempoAte } from '../formato';
import { nivelDaCota, PALAVRA_DO_NIVEL, type NivelDaCota } from '../uso/cota';

/**
 * Medidor de cota, no molde do `/usage` do Claude Code: rótulo, barra, "N%
 * usado" e quando reinicia — nessa ordem de leitura.
 *
 * Barra em CSS, sem biblioteca de gráfico: é um número por linha, e um pacote
 * de desenho custaria mais no painel do que vale.
 *
 * Duas regras que a tela inteira segue a partir daqui:
 *
 * - **Cor nunca sozinha.** Acima de 70% a barra fica âmbar e o texto diz
 *   "atenção"; acima de 90%, vermelha e "crítico". Quem não distingue as cores
 *   — ou lê por leitor de tela — recebe a mesma informação.
 * - **Sem dado é "sem dado", nunca 0%.** Zero afirma que nada foi gasto; a
 *   ausência de leitura não afirma nada, e desenhar uma barra vazia seria
 *   mentir com cara de boa notícia.
 */

const COR_DA_BARRA: Record<NivelDaCota, string> = {
  normal: 'bg-emerald-500',
  atencao: 'bg-amber-400',
  critico: 'bg-red-500',
};

const COR_DO_TEXTO: Record<NivelDaCota, string> = {
  normal: 'text-zinc-300',
  atencao: 'text-amber-300',
  critico: 'text-red-400',
};

/** O texto que acompanha a barra: `78% usado · atenção`, ou `sem dado`. */
export function textoDaCota(utilizacao: number | null): string {
  if (utilizacao === null) return 'sem dado';
  const palavra = PALAVRA_DO_NIVEL[nivelDaCota(utilizacao)];
  return `${porcento(utilizacao)} usado` + (palavra ? ` · ${palavra}` : '');
}

/**
 * Só a barra, para quem já escreve o número ao lado: o cabeçalho da conversa
 * e a coluna de % da lista de sessões.
 *
 * `role="meter"` com o valor e o texto: é o papel de "quanto de um todo", e
 * sem ele o leitor de tela anunciaria um `div` vazio. Sem dado não há medidor —
 * um `meter` sem valor é inválido —, e a trilha fica cinza e vazia.
 */
export function BarraDeCota({
  utilizacao,
  rotulo,
  grossa = false,
  className,
}: {
  utilizacao: number | null;
  rotulo: string;
  /** A do cartão de cota; a fina é a de linha de lista e de cabeçalho. */
  grossa?: boolean;
  /** Largura e margem. A altura é de `grossa`, para duas classes não disputarem. */
  className?: string;
}): React.JSX.Element {
  const trilha =
    'block overflow-hidden rounded-[4px] bg-zinc-800 ' +
    (grossa ? 'h-2 ' : 'h-1.5 ') +
    (className ?? '');

  if (utilizacao === null) {
    return <span className={trilha} data-sem-dado="" aria-hidden="true" />;
  }

  const valor = Math.round(utilizacao * 100);
  return (
    <span
      role="meter"
      aria-label={rotulo}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={valor}
      aria-valuetext={textoDaCota(utilizacao)}
      className={trilha}
    >
      <span
        className={'block h-full rounded-[4px] ' + COR_DA_BARRA[nivelDaCota(utilizacao)]}
        // A cota estourada (1,05) enche a barra; o texto é que diz quanto passou.
        style={{ width: Math.min(100, Math.max(0, valor)) + '%' }}
      />
    </span>
  );
}

export function MedidorDeCota({
  rotulo,
  utilizacao,
  reiniciaEm,
  detalhe,
}: {
  rotulo: string;
  /** Fração 0..1. `null` quando não há leitura — e aí a tela diz "sem dado". */
  utilizacao: number | null;
  reiniciaEm: string | null;
  detalhe?: string;
}): React.JSX.Element {
  const faltam = tempoAte(reiniciaEm);
  const quando = reiniciaEm ? new Date(reiniciaEm).toLocaleString('pt-BR') : undefined;
  const rodape = [faltam ? `reinicia em ${faltam}` : null, detalhe ?? null].filter(Boolean).join(' · ');

  return (
    <div data-testid="medidor-de-cota" className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-zinc-200">{rotulo}</span>
        <span
          className={
            'shrink-0 tabular-nums ' +
            (utilizacao === null ? 'text-zinc-500' : COR_DO_TEXTO[nivelDaCota(utilizacao)])
          }
        >
          {textoDaCota(utilizacao)}
        </span>
      </div>
      <BarraDeCota utilizacao={utilizacao} rotulo={rotulo} grossa />
      {rodape ? (
        // A hora exata fica no `title`: a frase relativa é o que se lê de
        // relance, e o `/usage` dá a data para quem quiser planejar.
        <p className="text-[11px] text-zinc-500" title={quando}>
          {rodape}
        </p>
      ) : null}
    </div>
  );
}
